
import { Check, Clock, FileText, MessageSquare, Phone, Video } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { formatRelative } from "date-fns";

interface OpportunityActivitiesProps {
  activities: any[];
  isLoading?: boolean;
}

export function OpportunityActivities({ activities, isLoading = false }: OpportunityActivitiesProps) {
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "";
    return formatRelative(new Date(dateString), new Date());
  };

  const getActivityIcon = (type: string) => {
    switch(type.toLowerCase()) {
      case 'call':
        return <Phone className="h-4 w-4" />;
      case 'meeting':
        return <Video className="h-4 w-4" />;
      case 'email':
        return <MessageSquare className="h-4 w-4" />;
      case 'document':
        return <FileText className="h-4 w-4" />;
      case 'status':
        return <Check className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-xl">Activity History</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-4 text-center text-muted-foreground">Loading activities...</div>
        ) : activities.length === 0 ? (
          <div className="py-4 text-center text-muted-foreground">No activities recorded</div>
        ) : (
          <div className="space-y-4">
            {activities.map((activity) => (
              <div key={activity.id} className="flex gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={activity.performer?.avatar_url} />
                  <AvatarFallback>
                    {activity.performer ? 
                      getInitials(activity.performer.first_name, activity.performer.last_name) : 
                      "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <div className="font-medium text-sm">
                      {activity.performer ? 
                        `${activity.performer.first_name} ${activity.performer.last_name}` : 
                        "System"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatDate(activity.performed_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="flex items-center gap-1">
                      {getActivityIcon(activity.activity_type)}
                      {activity.activity_type}
                    </Badge>
                  </div>
                  <div className="text-sm">{activity.description}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
